<template>
    <v-container :fluid="true" class="location-manager">
        <v-row class="h-100">
            <!-- Left Panel: Location List -->
            <v-col class="h-100" cols="12" md="3">
                <v-card class="full-card d-flex flex-column" elevation="3">
                    <v-card-title class="text-start pb-0">
                        <div class="d-flex align-center justify-space-between w-100">
                            <h4 class="text-h6 font-weight-bold">Places, Locations and Zones</h4>
                            <v-btn
                                color="primary" density="compact" icon="mdi-plus"
                                size="small" variant="flat"
                                :disabled="!selectedCompany"
                                @click="startCreateLocation"
                            >
                                <v-icon size="18">mdi-plus</v-icon>
                                <v-tooltip activator="parent" location="top">Add Location</v-tooltip>
                            </v-btn>
                        </div>
                        <company-select
                            v-if="user.type === 'admin'" v-model="selectedCompany" :items="companies"
                            class="mt-2" density="compact" hide-details label="Select Company"
                            variant="outlined"
                            @update:model-value="onCompanyChange"
                        />
                        <!-- Search -->
                        <v-text-field
                            v-if="selectedCompany"
                            v-model="searchQuery" class="mt-2" clearable density="compact"
                            hide-details label="Search locations..." prepend-inner-icon="mdi-magnify"
                            variant="outlined"
                        />
                    </v-card-title>

                    <v-divider class="mt-2"/>

                    <!-- Location List -->
                    <v-card-text class="py-0 text-start flex-grow-1 overflow-y-auto">
                        <!-- Loading -->
                        <div v-if="locationsLoading" class="d-flex justify-center py-8">
                            <v-progress-circular color="primary" indeterminate/>
                        </div>

                        <!-- Empty State -->
                        <div v-else-if="!filteredLocations.length" class="text-center py-8 text-medium-emphasis">
                            <v-icon color="grey-lighten-1" size="64">mdi-map-marker-off</v-icon>
                            <template v-if="searchQuery">
                                <p class="mt-2 text-body-2">No locations match your search</p>
                            </template>
                            <template v-else-if="selectedCompany">
                                <p class="mt-2 text-body-2 font-weight-medium">Get started with Locations</p>
                                <p class="mt-1 text-caption" style="max-width: 260px; margin: 0 auto;">
                                    Locations define your sites and facilities. Add zones inside locations
                                    for areas like parking lots or buildings, then add spots for individual spaces.
                                </p>
                                <v-btn
                                    class="mt-3" color="primary" prepend-icon="mdi-plus"
                                    size="small" variant="flat" @click="startCreateLocation"
                                >
                                    Create First Location
                                </v-btn>
                                <p class="mt-2 text-caption text-medium-emphasis">
                                    Draw a polygon on the map to define the boundary
                                </p>
                            </template>
                            <template v-else>
                                <p class="mt-2 text-body-2">Select a company to manage locations</p>
                            </template>
                        </div>

                        <!-- Location Tree -->
                        <v-treeview
                            v-else
                            :items="filteredLocations" :loadChildren="fetchChildren"
                            v-model:opened="opened"
                            activatable density="compact"
                            item-title="name" item-value="key" transition
                        >
                            <template v-slot:title="{ item }">
                                <!-- Inline rename mode -->
                                <v-text-field
                                    v-if="selectedItemForRename?.key === item.key"
                                    v-model="item.name"
                                    autofocus class="pt-3" density="compact" variant="outlined"
                                    @keydown.esc="resetName"
                                    @keydown.enter="updateName"
                                >
                                    <template v-slot:prepend>
                                        <v-icon :color="modelColor(item.model)" :icon="item.icon" size="small"/>
                                    </template>
                                    <template v-slot:append-inner>
                                        <v-btn density="compact" icon="mdi-close" size="x-small" variant="text"
                                               @click="resetName"/>
                                        <v-btn color="success" density="compact" icon="mdi-check" size="x-small"
                                               variant="text" @click="updateName"/>
                                    </template>
                                </v-text-field>

                                <!-- Normal display mode -->
                                <div
                                    v-else
                                    class="d-flex align-center w-100 tree-item-row"
                                    @click="selectAndCenter(item)"
                                    @contextmenu.prevent="(e) => showContextMenu(e, item)"
                                >
                                    <v-icon :color="modelColor(item.model)" :icon="item.icon" size="small"
                                            class="mr-2"/>
                                    <span class="text-body-2 text-truncate flex-grow-1">{{ item.name }}</span>
                                    <v-chip
                                        v-if="item.child_count" class="ml-1" color="grey" label
                                        size="x-small" variant="tonal"
                                    >{{ item.child_count }}
                                    </v-chip>
                                    <!-- Visibility toggle -->
                                    <v-btn
                                        class="ml-1" density="compact" icon size="x-small" variant="text"
                                        :color="hiddenKeys.has(item.key) ? 'grey' : ''"
                                        @click.stop="toggleVisibility(item)"
                                    >
                                        <v-icon size="14">{{ hiddenKeys.has(item.key) ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                                        <v-tooltip activator="parent" location="top">
                                            {{ hiddenKeys.has(item.key) ? 'Show on map' : 'Hide from map' }}
                                        </v-tooltip>
                                    </v-btn>
                                    <!-- Add child button -->
                                    <v-btn
                                        v-if="item.model !== 'spot'"
                                        class="ml-1" density="compact" icon size="x-small" variant="text"
                                        @click.stop="(e) => showCreateMenu(e, item)"
                                    >
                                        <v-icon size="16">mdi-plus</v-icon>
                                        <v-tooltip activator="parent" location="top">
                                            Add {{ item.model === 'location' ? 'Zone' : 'Spot' }}
                                        </v-tooltip>
                                    </v-btn>
                                </div>
                            </template>
                        </v-treeview>

                        <!-- Context Menu -->
                        <v-menu v-if="menu.state" v-model="menu.state" :target="menu.position" density="compact">
                            <v-list class="pa-0" density="compact">
                                <!-- Add child options -->
                                <v-list-item
                                    v-if="selectedItemForAction?.model === 'location'"
                                    prepend-icon="mdi-plus" title="Add Zone"
                                    @click="() => { createPolygon('manual'); }"
                                />
                                <v-list-item
                                    v-if="selectedItemForAction?.model === 'zone'"
                                    prepend-icon="mdi-plus" title="Add Spot"
                                    @click="() => { createPolygon('manual'); }"
                                />
                                <v-list-item
                                    v-if="selectedItemForAction?.model === 'zone'"
                                    prepend-icon="mdi-parking" title="Auto-Fill Parking"
                                    @click="openAutoFillDialog"
                                />
                                <v-divider v-if="selectedItemForAction?.model !== 'spot'"/>
                                <!-- Only once the API sends the setting: read as "None" and
                                     saved back, it would switch a place's live alerts off. -->
                                <v-list-item
                                    v-if="selectedItemForAction?.model === 'location' && selectedItemForAction?.enter_or_exit !== undefined"
                                             :title="`Alerts: ${placeAlertLabel(selectedItemForAction?.enter_or_exit)}`"
                                             prepend-icon="mdi-bell-outline"
                                             @click="openAlertsDialog(selectedItemForAction)"/>
                                <v-list-item prepend-icon="mdi-cursor-text" title="Rename"
                                             @click="startRename(selectedItemForAction)"/>
                                <v-list-item prepend-icon="mdi-pencil-outline" title="Edit Boundary"
                                             @click="menuAction('edit')"/>
                                <v-list-item prepend-icon="mdi-cursor-move" title="Move"
                                             @click="menuAction('move')"/>
                                <v-list-item
                                    :prepend-icon="hiddenKeys.has(selectedItemForAction?.key) ? 'mdi-eye' : 'mdi-eye-off'"
                                    :title="hiddenKeys.has(selectedItemForAction?.key) ? 'Show on Map' : 'Hide from Map'"
                                    @click="toggleVisibility(selectedItemForAction)"
                                />
                                <v-divider/>
                                <v-list-item class="text-error" prepend-icon="mdi-delete-outline" title="Delete"
                                             @click="menuAction('delete')"/>
                            </v-list>
                        </v-menu>

                        <!-- Create Child Menu -->
                        <v-menu v-if="createMenu.state" v-model="createMenu.state" :target="createMenu.position"
                                density="compact">
                            <v-list class="pa-0" density="compact">
                                <v-list-item prepend-icon="mdi-draw" title="Draw Manually"
                                             @click="createPolygon('manual')"/>
                                <v-list-item prepend-icon="mdi-auto-fix" title="Auto Generate"
                                             @click="createPolygon('auto')"/>
                                <v-list-item
                                    v-if="selectedItemForAction?.model === 'zone'"
                                    prepend-icon="mdi-parking" title="Auto-Fill Parking Spaces"
                                    @click="openAutoFillDialog"
                                />
                            </v-list>
                        </v-menu>
                    </v-card-text>

                    <!-- Legend -->
                    <v-divider/>
                    <v-card-actions class="px-4 py-2 justify-center">
                        <v-chip class="mr-1" color="#2196F3" label size="x-small" variant="flat">
                            <v-icon size="10" start>mdi-circle</v-icon>
                            Location
                        </v-chip>
                        <v-chip class="mr-1" color="#FF5722" label size="x-small" variant="flat">
                            <v-icon size="10" start>mdi-circle</v-icon>
                            Zone
                        </v-chip>
                        <v-chip color="#673AB7" label size="x-small" variant="flat">
                            <v-icon size="10" start>mdi-circle</v-icon>
                            Spot
                        </v-chip>
                    </v-card-actions>
                </v-card>
            </v-col>

            <!-- Right Panel: Map -->
            <v-col class="h-100" cols="12" md="9">
                <v-card class="full-card" elevation="3">
                    <!-- Map toolbar overlay -->
                    <div class="map-toolbar">
                        <v-chip
                            v-if="selectedItem" class="mr-2" closable color="primary"
                            size="small" variant="elevated"
                            @click:close="deselectItem"
                        >
                            <v-icon :color="modelColor(selectedItem.model)" size="14" start>{{
                                    selectedItem.icon
                                }}
                            </v-icon>
                            {{ selectedItem.name }}
                        </v-chip>
                        <v-chip
                            v-if="editMode" closable color="warning" size="small"
                            variant="elevated" @click:close="cancelEdit"
                        >
                            <v-icon size="14" start>mdi-pencil</v-icon>
                            Editing
                        </v-chip>
                        <v-chip
                            v-if="moveMode" closable color="info" size="small"
                            variant="elevated" @click:close="cancelMove"
                        >
                            <v-icon size="14" start>mdi-cursor-move</v-icon>
                            Moving
                        </v-chip>
                        <v-chip
                            v-if="hiddenKeys.size" clickable color="grey-darken-1" size="small"
                            variant="elevated" @click="showAllLayers"
                        >
                            <v-icon size="14" start>mdi-eye-off</v-icon>
                            {{ hiddenKeys.size }} hidden
                            <v-tooltip activator="parent" location="top">Click to show all</v-tooltip>
                        </v-chip>
                    </div>

                    <!-- Map right-click menu (show all hidden) -->
                    <v-menu v-if="mapMenu.state" v-model="mapMenu.state" :target="mapMenu.position"
                            density="compact">
                        <v-list class="pa-0" density="compact">
                            <v-list-item prepend-icon="mdi-eye" :title="`Show All (${hiddenKeys.size} hidden)`"
                                         @click="showAllLayers"/>
                        </v-list>
                    </v-menu>

                    <!-- Base map: street map, satellite (with place names) or terrain -->
                    <v-btn-toggle
                        v-model="baseLayer" class="map-layer-toggle" color="primary" density="compact"
                        divided mandatory variant="elevated" @update:model-value="saveBaseLayer"
                    >
                        <v-btn v-for="layer in BASE_LAYERS" :key="layer.value" :aria-label="layer.label"
                               :value="layer.value" size="small">
                            <v-icon>{{ layer.icon }}</v-icon>
                            <v-tooltip activator="parent" location="top">{{ layer.label }}</v-tooltip>
                        </v-btn>
                    </v-btn-toggle>

                    <l-map ref="mapContainer" :center="mapCenter" :max-zoom="18" :zoom="10" class="full-card"
                           @ready="mapIsReady">
                        <l-tile-layer v-if="baseLayer === 'map'" :url="$mapTiles.street"/>
                        <template v-else-if="baseLayer === 'satellite'">
                            <l-tile-layer
                                :url="$mapTiles.satellite"
                                :options="{ maxZoom: 19 }"/>
                            <l-tile-layer
                                :url="$mapTiles.satelliteLabels"
                                :options="{ maxZoom: 19 }"/>
                        </template>
                        <l-tile-layer
                            v-else
                            :url="$mapTiles.topo"
                            :options="{ maxZoom: 19 }"/>
                    </l-map>
                </v-card>
            </v-col>
        </v-row>
    </v-container>

    <!-- Place alerts for an existing location -->
    <v-dialog v-model="alertsDialog.open" max-width="420" persistent>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <span class="text-h6">Alerts for {{ alertsDialog.item?.name }}</span>
                <v-btn density="compact" icon="mdi-close" variant="text" @click="alertsDialog.open = false"/>
            </v-card-title>
            <v-divider/>
            <v-card-text>
                <v-btn-toggle v-model="alertsDialog.value" color="primary" density="compact"
                              divided mandatory variant="outlined">
                    <v-btn v-for="option in placeAlertOptions" :key="option.value" :value="option.value"
                           size="small">{{ option.title }}
                    </v-btn>
                </v-btn-toggle>
                <div class="text-caption text-grey mt-2">{{ placeAlertHint(alertsDialog.value) }}</div>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <v-btn size="small" variant="text" @click="alertsDialog.open = false">Cancel</v-btn>
                <v-btn :loading="alertsDialog.saving" color="primary" size="small" variant="flat"
                       @click="saveAlerts">Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialogSwitch" max-width="420" persistent>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <span class="text-h6">
                    {{ editingItem ? 'Edit' : 'New' }}
                    {{ parentPolygon ? (parentPolygon.model === 'location' ? 'Zone' : 'Spot') : 'Location' }}
                </span>
                <v-btn density="compact" icon="mdi-close" variant="text" @click="resetPolygonSetting"/>
            </v-card-title>
            <v-divider/>
            <v-card-text>
                <v-text-field
                    v-model="formData.name" :rules="[vRules.required, vRules.maxLength(40)]"
                    autofocus class="mb-2" density="compact" hide-details="auto"
                    label="Name" variant="outlined"
                />
                <div v-if="!parentPolygon && !editingItem" class="mt-3">
                    <div class="text-subtitle-2 mb-1">Alerts</div>
                    <v-btn-toggle v-model="formData.enter_or_exit" color="primary" density="compact"
                                  divided mandatory variant="outlined">
                        <v-btn v-for="option in placeAlertOptions" :key="option.value" :value="option.value"
                               size="small">{{ option.title }}
                        </v-btn>
                    </v-btn-toggle>
                    <div class="text-caption text-grey mt-1">{{ placeAlertHint(formData.enter_or_exit) }}</div>
                </div>
                <v-select
                    v-if="parentPolygon" v-model="formData.type"
                    :items="parentPolygon.model === 'location' ? zoneTypes : spotTypes"
                    :rules="[vRules.required]" class="mt-3" density="compact"
                    hide-details="auto" item-title="name" item-value="id"
                    label="Type" variant="outlined"
                />
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <v-btn size="small" variant="text" @click="resetPolygonSetting">Cancel</v-btn>
                <v-btn :loading="saving" color="primary" size="small" variant="flat" @click="savePolygon">
                    {{ editingItem ? 'Update' : 'Create' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
            <v-card-title class="text-h6">Delete {{ deleteTarget?.model ? deleteTarget.model.charAt(0).toUpperCase() + deleteTarget.model.slice(1) : '' }}?</v-card-title>
            <v-card-text>
                Are you sure you want to delete <strong>"{{ deleteTarget?.name }}"</strong>?
                <span v-if="deleteTarget?.child_count">
                    This will also delete {{ deleteTarget.child_count }}
                    {{ deleteTarget.model === 'location' ? 'zone(s)' : 'spot(s)' }} inside it.
                </span>
                This action cannot be undone.
            </v-card-text>
            <v-card-actions>
                <v-spacer/>
                <v-btn size="small" variant="text" @click="deleteDialog = false">Cancel</v-btn>
                <v-btn :loading="deleting" color="error" size="small" variant="flat"
                       @click="confirmDelete">Delete
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Auto-Fill Parking Dialog -->
    <v-dialog v-model="autoFillDialog" max-width="440" persistent>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <span class="text-h6">Auto-Fill Parking Spaces</span>
                <v-btn density="compact" icon="mdi-close" variant="text" @click="cancelAutoFill"/>
            </v-card-title>
            <v-divider/>
            <v-card-text>
                <p class="text-body-2 text-medium-emphasis mb-3">
                    Generate evenly spaced parking spots inside
                    <strong>"{{ autoFillZone?.name }}"</strong>
                </p>
                <v-text-field
                    v-model.number="autoFillForm.count" :rules="[vRules.required, vRules.numeric]"
                    class="mb-2" density="compact" hide-details="auto"
                    label="Number of Spaces" min="1" type="number" variant="outlined"
                />
                <v-text-field
                    v-model.number="autoFillForm.rows" :rules="[vRules.required, vRules.numeric]"
                    class="mb-2" density="compact" hide-details="auto"
                    label="Number of Rows" min="1" type="number" variant="outlined"
                />
                <v-text-field
                    v-model="autoFillForm.prefix" class="mb-2" density="compact"
                    hide-details="auto" label="Name Prefix (e.g. A, Bay)" variant="outlined"
                />
                <v-select
                    v-model="autoFillForm.type" :items="spotTypes" class="mb-2"
                    density="compact" hide-details="auto" item-title="name"
                    item-value="id" label="Spot Type" variant="outlined"
                />
                <v-select
                    v-model="autoFillForm.orientation" :items="orientationOptions"
                    density="compact" hide-details="auto" item-title="name"
                    item-value="id" label="Orientation" variant="outlined"
                />
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-chip v-if="autoFillPreviewCount > 0" color="info" size="small" variant="tonal">
                    {{ autoFillPreviewCount }} spaces fit
                </v-chip>
                <v-spacer/>
                <v-btn size="small" variant="text" @click="cancelAutoFill">Cancel</v-btn>
                <v-btn size="small" variant="tonal" @click="previewAutoFill">Preview</v-btn>
                <v-btn
                    :disabled="autoFillPreviewCount === 0" :loading="saving"
                    color="primary" size="small" variant="flat" @click="confirmAutoFill"
                >
                    Create {{ autoFillPreviewCount || '' }} Spaces
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import L from "leaflet";
import "leaflet-path-drag";
import {computed, onMounted, onUnmounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {placeAlertHint, placeAlertLabel, placeAlertOptions} from "@/composables/placeAlerts";
import {useAuthStore} from "@/stores/authStore";
import {VTreeview} from 'vuetify/labs/VTreeview';
import {useSnackbarStore} from "@/stores/snackBarStore";
import {LMap, LTileLayer} from "@vue-leaflet/vue-leaflet";
import "leaflet-geosearch/assets/css/leaflet.css";
import {addressSearchControl} from "@/composables/addressSearch";
import CompanySelect from "@/components/globals/company-select.vue";

// --- State ---
const map = ref();
const opened = ref([]);
const mapContainer = ref();
const {user} = useAuthStore();
const {showSnackbar} = useSnackbarStore();

const newPolygon = ref(null);
const menu = ref({state: false});
const createMenu = ref({state: false});
const mapMenu = ref({state: false});
const searchQuery = ref('');

// Base map choice, remembered per browser.
const BASE_LAYERS = [
    {value: 'map', label: 'Map', icon: 'mdi-map-outline'},
    {value: 'satellite', label: 'Satellite', icon: 'mdi-satellite-variant'},
    {value: 'terrain', label: 'Terrain', icon: 'mdi-terrain'},
];
const BASE_LAYER_KEY = 'locations.baseLayer';
const readBaseLayer = () => {
    try {
        const saved = localStorage.getItem(BASE_LAYER_KEY);
        return BASE_LAYERS.some(l => l.value === saved) ? saved : 'map';
    } catch {
        return 'map';
    }
};
const baseLayer = ref(readBaseLayer());
const saveBaseLayer = (value) => {
    try {
        localStorage.setItem(BASE_LAYER_KEY, value);
    } catch {
        // Private mode or blocked storage: the choice just is not remembered.
    }
};
const locationsLoading = ref(false);
const saving = ref(false);
const deleting = ref(false);
const deleteDialog = ref(false);
const deleteTarget = ref(null);
const selectedItem = ref(null);
const editingItem = ref(null);

const formData = reactive({
    name: null,
    type: null,
    polygon_coordinates: null,
    // A new location alerts on arrive and leave, as in the Fleet app.
    enter_or_exit: 3,
});
const editMode = ref(false);
const moveMode = ref(false);
const dialogSwitch = ref(false);
const autoFillDialog = ref(false);
const autoFillZone = ref(null);
const autoFillPreviewCount = ref(0);
const autoFillPreviewLayers = [];
const autoFillForm = reactive({
    count: 10,
    rows: 2,
    prefix: 'P',
    type: 0,
    orientation: 0,
});
const orientationOptions = [
    {id: 0, name: 'Horizontal (along longest edge)'},
    {id: 90, name: 'Vertical (perpendicular)'},
    {id: 45, name: '45° Angled'},
    {id: 60, name: '60° Angled'},
];
const drawnItems = new L.FeatureGroup();
const activeLayer = new L.FeatureGroup();
const drawControl = new L.Control.Draw({
    draw: {
        polygon: false,
        circle: false,
        marker: false,
        polyline: false,
        rectangle: false,
        circlemarker: false,
    },
    edit: {
        remove: false,
        featureGroup: activeLayer,
    },
});

const orgName = ref('');
const movingPolygon = ref(null);
const movingOriginalLatLngs = ref(null);
const selectedCompany = ref();
const selectedItemForRename = ref();
const selectedItemForAction = ref();
const companies = ref([]);
const locations = reactive([]);
const parentPolygon = ref(null);
const mapCenter = ref([39.8283, -98.5795]); // Center of US

const zoneTypes = [
    {id: 0, name: 'Open Parking Lot'},
    {id: 1, name: 'Covered Parking'},
    {id: 2, name: 'Building'},
    {id: 3, name: 'Work Site'},
    {id: 4, name: 'Multi-Level Garage'},
    {id: 5, name: 'Indoor Facility'},
    {id: 6, name: 'Multi-Level Indoor'},
];
const spotTypes = [
    {id: 0, name: 'Parking Space'},
    {id: 1, name: 'Loading Dock / Ramp'},
    {id: 2, name: 'Reserved Space'},
    {id: 3, name: 'Staging Area'},
    {id: 4, name: 'No-Parking Zone'},
];

const hiddenKeys = reactive(new Set());
const boundaryMask = ref(null);
const editingParentItem = ref(null);

const modelColor = (model) => model === 'location' ? '#2196F3' : (model === 'zone' ? '#FF5722' : '#673AB7');

const filteredLocations = computed(() => {
    if (!searchQuery.value) return locations;
    const q = searchQuery.value.toLowerCase();
    const matchesDeep = (item) => {
        if (item.name.toLowerCase().includes(q)) return true;
        return item.children?.some(child => matchesDeep(child)) ?? false;
    };
    return locations.filter(loc => matchesDeep(loc));
});

// --- Lifecycle ---
const handleKeyDown = (e) => {
    // Ignore if typing in an input field
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;

    if (e.key === 'Escape') {
        if (editMode.value) cancelEdit();
        else if (moveMode.value) cancelMove();
        else if (dialogSwitch.value) resetPolygonSetting();
        else if (autoFillDialog.value) cancelAutoFill();
    } else if (e.key === 'Delete') {
        if (editMode.value || moveMode.value || dialogSwitch.value) return;
        if (selectedItem.value) {
            deleteTarget.value = selectedItem.value;
            deleteDialog.value = true;
        }
    }
};

onMounted(() => {
    document.addEventListener('keydown', handleKeyDown);
    if (user.type === 'admin') {
        fetchCompaniesList();
    } else {
        selectedCompany.value = user.company.id;
        fetchLocations();
    }
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown);
});

// --- Map Setup ---
const mapIsReady = () => {
    map.value = mapContainer.value.leafletObject;
    map.value.attributionControl.remove();
    map.value.addControl(addressSearchControl(map.value, {
        onNotFound: (query) => showSnackbar(
            `No match for "${query}". Try a street and city, or a place name.`,
            'warning',
            6000
        ),
    }));
    map.value.addLayer(drawnItems);
    map.value.addControl(drawControl);

    // Reject vertices placed outside parent boundary during child drawing
    map.value.on(L.Draw.Event.DRAWVERTEX, function () {
        if (!parentPolygon.value) return;
        const handler = drawControl._toolbars.draw._modes.polygon.handler;
        const markers = handler._markers;
        if (!markers || !markers.length) return;

        const lastMarker = markers[markers.length - 1];
        const ll = lastMarker.getLatLng();
        const parentCoords = parentPolygon.value.polygon_coordinates.map(c => [c.latitude, c.longitude]);
        if (!pointInPolygon(parentCoords, [ll.lat, ll.lng])) {
            handler.deleteLastVertex();
            showSnackbar('Click inside the parent boundary', 'warning');
        }
    });

    map.value.on('draw:created', function (e) {
        // Keep the drawn layer visible until save/cancel
        drawnItems.addLayer(e.layer);
        newPolygon.value = e.layer;
        showCreatePolygonPopup(e.layer.getLatLngs()[0]);
    });

    map.value.on('draw:edited', function (e) {
        if (newPolygon.value) {
            showCreatePolygonPopup(newPolygon.value.getLatLngs()[0]);
        } else {
            e.layers.eachLayer(function (polygon) {
                const [location, parent] = findObjectByKey(locations, polygon['options']['key'], true);

                if (validatePolygon(polygon, parent, location)) {
                    const url = buildApiUrl(location, parent);
                    try {
                        updatePolygon(url, {
                            name: location.name,
                            location_changed: true,
                            type: location.type_id ?? null,
                            polygon_coordinates: polygon.getLatLngs()[0]
                        }, location.key).catch(() => {
                            drawControl._toolbars.edit._modes.edit.handler.revertLayers();
                        });
                    } catch (e) {
                        drawControl._toolbars.edit._modes.edit.handler.revertLayers();
                    }
                } else {
                    drawControl._toolbars.edit._modes.edit.handler.revertLayers();
                }
            });
        }
    });

    // Constrain vertex movement inside parent during editing
    map.value.on(L.Draw.Event.EDITVERTEX, function () {
        if (!editingParentItem.value) return;
        const parentCoords = editingParentItem.value.polygon_coordinates.map(c => [c.latitude, c.longitude]);
        activeLayer.eachLayer(polygon => {
            const verts = polygon.getLatLngs()[0];
            let anyOutside = false;
            for (const v of verts) {
                if (!pointInPolygon(parentCoords, [v.lat, v.lng])) {
                    anyOutside = true;
                    break;
                }
            }
            if (anyOutside) {
                showSnackbar('Keep vertices inside the parent boundary', 'warning');
            }
        });
    });

    map.value.on('draw:editstop', function () {
        editMode.value = false;
        removeBoundaryMask();
        editingParentItem.value = null;
    });

    map.value.on('draw:toolbarclosed', function () {
        if (!dialogSwitch.value) {
            resetPolygonSetting();
        }
    });

    // Right-click on map background to show all hidden layers
    map.value.on('contextmenu', function (e) {
        if (hiddenKeys.size > 0) {
            mapMenu.value = {state: true, position: [e.originalEvent.clientX, e.originalEvent.clientY]};
        }
    });

    map.value.on('draw:deleted', function () {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });
    });

    if (selectedCompany.value) togglePolygonTool(true);
};

// --- API Fetching ---
const fetchCompaniesList = () => {
    send('get', 'companies/list')
        .then((response) => {
            companies.value = response.data.data;
        }).catch(() => {
        showSnackbar('Companies list could not be loaded', 'error');
    });
};

const fetchLocations = () => {
    locationsLoading.value = true;
    send('get', `companies/${selectedCompany.value}/locations/list`)
        .then((response) => {
            locations.length = 0;
            locations.push(...response.data.data.map(item => ({
                ...item,
                icon: 'mdi-map-marker',
                model: 'location',
                key: `location-${item.id}`,
                children: item.child_count ? [] : null,
            })));
            updateMap(locations);
            if (locations.length && locations[0].polygon_coordinates?.length) {
                fitAllLocations();
            }
        })
        .catch(() => {
            showSnackbar('Locations could not be loaded', 'error');
        })
        .finally(() => {
            locationsLoading.value = false;
        });
};

const fetchChildren = async (location) => {
    // Guard against duplicate fetches
    if (location._childrenLoaded) return;

    const url = location.model === 'location'
        ? `companies/locations/${location.id}/zones/list`
        : `companies/locations/zones/${location.id}/spots/list`;

    try {
        const response = await send('get', url);
        location.children.push(...response.data.data.map(child => ({
            ...child,
            children: child.child_count ? [] : null,
            parent: location.key,
            model: location.model === 'location' ? 'zone' : 'spot',
            key: `${location.model === 'location' ? 'zone' : 'spot'}-${child.id}`,
            icon: location.model === 'location' ? 'mdi-map-marker-radius' : 'mdi-map-marker-circle',
        })));
        location._childrenLoaded = true;
        updateMap(location.children);
        if (location.polygon_coordinates?.length) {
            centerMap(location);
        }
    } catch (error) {
        console.error('Error fetching children:', error);
        showSnackbar('Could not load items', 'error');
    }
};

// --- Map Drawing ---
const updateMap = (source) => {
    if (source.length) {
        source.forEach(loc => drawPolygonOnMap(loc));
        drawnItems.addTo(map.value);
    }
};

const drawPolygonOnMap = (location) => {
    const coords = location.polygon_coordinates?.map(c => ({lat: c.latitude, lng: c.longitude})) ?? [];
    if (!coords.length) return;

    const polygon = L.polygon(coords, {
        id: location.id,
        draggable: true,
        key: location.key,
        model: location.model,
        parent: location.parent ?? null,
        color: modelColor(location.model),
        weight: 3,
        fillOpacity: 0.15,
    });

    // Tooltip with name
    polygon.bindTooltip(location.name, {
        permanent: false,
        direction: 'center',
        className: 'polygon-label',
    });

    bindPopup(polygon);
    drawnItems.addLayer(polygon);
    polygon.dragging.disable();

    // Hide if parent or self is hidden
    if (hiddenKeys.has(location.key) || (location.parent && hiddenKeys.has(location.parent))) {
        hiddenKeys.add(location.key);
        map.value.removeLayer(polygon);
    }
};

const removeAllLayers = () => {
    drawnItems.eachLayer(layer => drawnItems.removeLayer(layer));
};

const centerMap = (item) => {
    if (item.id) {
        const layer = findLayer(item.key);
        if (layer) map.value.fitBounds(layer.getBounds(), {padding: [40, 40]});
    } else {
        map.value.fitBounds(item.getBounds(), {padding: [40, 40]});
    }
};

const fitAllLocations = () => {
    if (Object.keys(drawnItems._layers).length) {
        map.value.fitBounds(drawnItems.getBounds(), {padding: [40, 40]});
    }
};

const togglePolygonTool = (state) => {
    if (!map.value) return;
    if (state) {
        drawControl.setDrawingOptions({
            polygon: {
                showArea: true,
                allowIntersection: false,
                shapeOptions: {weight: 4, color: '#146210'},
                drawError: {
                    color: '#9d1212',
                    message: '<strong>Invalid polygon!</strong> Lines cannot cross.'
                }
            }
        });
    } else {
        drawControl.setDrawingOptions({polygon: false});
    }
    map.value.addControl(drawControl);
};

// --- Selection & Navigation ---
const selectAndCenter = (item) => {
    selectedItem.value = item;
    centerMap(item);
    // Highlight selected polygon
    drawnItems.eachLayer(layer => {
        if (layer.options.key === item.key) {
            layer.setStyle({weight: 5, fillOpacity: 0.25});
        } else {
            layer.setStyle({weight: 3, fillOpacity: 0.15});
        }
    });
};

const deselectItem = () => {
    selectedItem.value = null;
    drawnItems.eachLayer(layer => layer.setStyle({weight: 3, fillOpacity: 0.15}));
};

// --- Visibility Toggle ---
const toggleVisibility = (item) => {
    const hiding = !hiddenKeys.has(item.key);
    setLayerVisibility(item, hiding);
};

const setLayerVisibility = (item, hide) => {
    if (hide) {
        hiddenKeys.add(item.key);
        const layer = findLayer(item.key);
        if (layer && map.value.hasLayer(layer)) map.value.removeLayer(layer);
    } else {
        hiddenKeys.delete(item.key);
        const layer = findLayer(item.key);
        if (layer && !map.value.hasLayer(layer)) map.value.addLayer(layer);
    }
    // Cascade to children
    if (item.children) {
        for (const child of item.children) {
            setLayerVisibility(child, hide);
        }
    }
};

const showAllLayers = () => {
    hiddenKeys.clear();
    drawnItems.eachLayer(layer => {
        if (!map.value.hasLayer(layer)) map.value.addLayer(layer);
    });
};

const onCompanyChange = () => {
    // Reset all active operations
    if (editMode.value) cancelEdit();
    if (moveMode.value) cancelMove();
    clearAutoFillPreview();
    autoFillDialog.value = false;
    autoFillZone.value = null;
    resetPolygonSetting();
    opened.value = [];
    selectedItem.value = null;
    selectedItemForAction.value = null;
    selectedItemForRename.value = null;
    deleteDialog.value = false;
    deleteTarget.value = null;
    hiddenKeys.clear();
    removeAllLayers();
    fetchLocations();
    if (selectedCompany.value) togglePolygonTool(true);
};

// --- Create Location ---
const startCreateLocation = () => {
    if (editMode.value || moveMode.value) {
        showSnackbar('Finish current operation first', 'error');
        return;
    }
    parentPolygon.value = null;
    editingItem.value = null;
    drawControl._toolbars.draw._modes.polygon.handler.enable();
};

const createPolygon = (mode = 'manual') => {
    if (editMode.value || moveMode.value) {
        showSnackbar('Finish current operation first', 'error');
        return;
    }
    if (!selectedItemForAction.value) {
        showSnackbar('Please select a parent first', 'error');
        return;
    }
    const parent = selectedItemForAction.value;
    resetPolygonSetting();
    opened.value = [parent?.parent, parent.key];
    if (parent.child_count && !parent._childrenLoaded) {
        fetchChildren(parent);
    }

    centerMap(parent);
    parentPolygon.value = parent;

    const parentLayer = findLayer(parentPolygon.value.key);
    if (parentLayer) {
        parentLayer.setStyle({weight: 6, dashArray: '10, 20'});
    }

    // Show boundary mask to constrain drawing to parent
    showBoundaryMask(parent);

    if (mode === 'manual') {
        drawControl._toolbars.draw._modes.polygon.handler.enable();
    } else {
        const polygon = createInnerRectangle(parentLayer);
        polygon.on('dragend', () => {
            newPolygon.value = polygon;
            editPolygon(newPolygon.value);
        });
        newPolygon.value = polygon;
        editPolygon(newPolygon.value);
        newPolygon.value.addTo(map.value);
    }
};

const showCreatePolygonPopup = (latLongs) => {
    if (parentPolygon.value) {
        const valid = isChildInsideParent(
            parentPolygon.value.polygon_coordinates.map(c => ({lat: c.latitude, lng: c.longitude})),
            latLongs
        );
        if (!valid) {
            resetPolygonSetting();
            showSnackbar("Polygon must stay within the parent boundary.", 'error');
            return;
        }
    }
    formData.polygon_coordinates = latLongs;
    dialogSwitch.value = true;
};

const savePolygon = () => {
    if (!formData.name) {
        showSnackbar('Name is required', 'error');
        return;
    }
    if (parentPolygon.value && formData.type == null) {
        showSnackbar('Type is required', 'error');
        return;
    }

    saving.value = true;

    if (newPolygon.value) {
        drawnItems.removeLayer(newPolygon.value);
        if (map.value.hasLayer(newPolygon.value)) {
            map.value.removeLayer(newPolygon.value);
        }
        newPolygon.value = null;
    }

    let url = `companies/locations/${selectedCompany.value}`;
    if (parentPolygon.value) {
        url = parentPolygon.value.model === 'location'
            ? `companies/locations/${parentPolygon.value.id}/zones`
            : `companies/locations/zones/${parentPolygon.value.id}/spots`;
    }

    const payload = parentPolygon.value
        ? {name: formData.name, type: formData.type, polygon_coordinates: formData.polygon_coordinates}
        : formData;

    send('post', url, payload)
        .then(async (response) => {
            if (response.data.success) {
                const data = response.data.data;
                const isChild = !!parentPolygon.value;
                const model = isChild
                    ? (parentPolygon.value.model === 'location' ? 'zone' : 'spot')
                    : 'location';
                const polygon = {
                    ...data,
                    parent: isChild ? parentPolygon.value.key : null,
                    icon: model === 'location' ? 'mdi-map-marker' : (model === 'zone' ? 'mdi-map-marker-radius' : 'mdi-map-marker-circle'),
                    model,
                    key: `${model}-${data.id}`,
                    children: null,
                    child_count: 0,
                };

                if (isChild) {
                    if (parentPolygon.value.children) {
                        parentPolygon.value.children.push(polygon);
                    } else {
                        parentPolygon.value.children = [polygon];
                    }
                    parentPolygon.value.child_count = (parentPolygon.value.child_count || 0) + 1;
                    await new Promise(r => setTimeout(r, 300));
                    opened.value = [parentPolygon.value?.parent, parentPolygon.value.key];
                } else {
                    locations.push(polygon);
                }

                drawPolygonOnMap(polygon);
                drawnItems.addTo(map.value);
                resetPolygonSetting();
                showSnackbar(response.data.message);
            }
        })
        .catch(function (errorResponse) {
            handleApiError(errorResponse);
            // Reset parent styling on failure
            if (parentPolygon.value) {
                const parentLayer = findLayer(parentPolygon.value.key);
                if (parentLayer) parentLayer.setStyle({weight: 3, dashArray: '0'});
                parentPolygon.value = null;
            }
        })
        .finally(() => {
            saving.value = false;
        });
};

const resetPolygonSetting = () => {
    if (newPolygon.value) {
        drawnItems.removeLayer(newPolygon.value);
        if (map.value.hasLayer(newPolygon.value)) {
            map.value.removeLayer(newPolygon.value);
        }
        newPolygon.value = null;
    }

    if (parentPolygon.value) {
        const parentLayer = findLayer(parentPolygon.value.key);
        if (parentLayer) {
            parentLayer.setStyle({weight: 3, dashArray: '0'});
        }
    }

    removeBoundaryMask();
    formData.name = null;
    formData.type = null;
    formData.enter_or_exit = 3;
    formData.polygon_coordinates = null;
    parentPolygon.value = null;
    editingItem.value = null;
    dialogSwitch.value = false;
};

const createInnerRectangle = (parentPolygon) => {
    const bounds = parentPolygon.getBounds();
    const factor = 0.25;

    const minLat = bounds.getSouth(), maxLat = bounds.getNorth();
    const minLng = bounds.getWest(), maxLng = bounds.getEast();
    const cLat = (minLat + maxLat) / 2, cLng = (minLng + maxLng) / 2;
    const w = (maxLng - minLng) * factor, h = (maxLat - minLat) * factor;

    return L.polygon([
        [cLat - h / 2, cLng - w / 2],
        [cLat - h / 2, cLng + w / 2],
        [cLat + h / 2, cLng + w / 2],
        [cLat + h / 2, cLng - w / 2],
        [cLat - h / 2, cLng - w / 2],
    ], {color: '#146210', draggable: true});
};

// --- Place alerts ---
const alertsDialog = reactive({open: false, item: null, value: 0, saving: false});

const openAlertsDialog = (item) => {
    alertsDialog.item = item;
    alertsDialog.value = Number(item?.enter_or_exit ?? 0);
    alertsDialog.open = true;
};

const saveAlerts = () => {
    const item = alertsDialog.item;
    if (!item) return;
    alertsDialog.saving = true;
    // The update route validates the whole location, so name and boundary go
    // too; without location_changed the boundary is not rewritten.
    send('put', `companies/locations/${item.id}`, {
        name: item.name,
        enter_or_exit: alertsDialog.value,
        polygon_coordinates: item.polygon_coordinates.map(c => ({lat: c.latitude, lng: c.longitude})),
    })
        .then((response) => {
            if (response.data.success) {
                item.enter_or_exit = alertsDialog.value;
                alertsDialog.open = false;
                showSnackbar(response.data.message);
            }
        })
        .catch(handleApiError)
        .finally(() => {
            alertsDialog.saving = false;
        });
};

// --- Rename ---
const startRename = (item) => {
    if (editMode.value || moveMode.value) return;
    selectedItemForRename.value = item;
    orgName.value = item.name;
};

const resetName = () => {
    if (selectedItemForRename.value) {
        selectedItemForRename.value.name = orgName.value;
    }
    selectedItemForRename.value = null;
};

const updateName = () => {
    if (!selectedItemForRename.value) return;

    const item = selectedItemForRename.value;
    const url = buildApiUrl(item);

    send("put", url, {
        name: item.name,
        type: item.type_id ?? null,
        polygon_coordinates: item.polygon_coordinates.map(c => ({lat: c.latitude, lng: c.longitude})),
    })
        .then((response) => {
            if (response.data.success) {
                // Update tooltip
                const layer = findLayer(item.key);
                if (layer) layer.setTooltipContent(item.name);
                showSnackbar(response.data.message);
            }
        })
        .catch((errorResponse) => {
            handleApiError(errorResponse);
            item.name = orgName.value;
        })
        .finally(() => {
            selectedItemForRename.value = null;
            orgName.value = null;
        });
};

// --- Context Menu & Actions ---
const showContextMenu = (e, item) => {
    e.preventDefault();
    menu.value = {state: true, position: [e.clientX, e.clientY]};
    selectedItemForAction.value = item;
};

const showCreateMenu = (e, item) => {
    e.preventDefault();
    createMenu.value = {state: true, position: [e.clientX, e.clientY]};
    selectedItemForAction.value = item;
};

const menuAction = (type) => {
    if (!selectedItemForAction.value) return;
    centerMap(selectedItemForAction.value);
    const layer = findLayer(selectedItemForAction.value.key);

    switch (type) {
        case 'edit':
            if (layer) editPolygon(layer);
            break;
        case 'move':
            if (layer) movePolygon(layer);
            break;
        case 'delete':
            deleteTarget.value = selectedItemForAction.value;
            deleteDialog.value = true;
            break;
    }
};

// --- Popup on polygons ---
const bindPopup = (polygon) => {
    polygon.on('contextmenu', (e) => {
        L.DomEvent.stopPropagation(e);
        const item = findObjectByKey(locations, polygon.options.key);
        if (item) {
            selectedItemForAction.value = item;
            menu.value = {state: true, position: [e.originalEvent.clientX, e.originalEvent.clientY]};
        }
    });

    polygon.on('click', () => {
        const item = findObjectByKey(locations, polygon.options.key);
        if (item) {
            selectedItem.value = item;
            selectAndCenter(item);
        }
    });
};

// --- Edit & Move ---
const editPolygon = (polygon) => {
    if (moveMode.value) {
        showSnackbar("Finish moving before editing", 'error');
        return;
    }
    editMode.value = true;
    activeLayer.eachLayer(layer => activeLayer.removeLayer(layer));
    activeLayer.addLayer(polygon);

    // Show boundary mask for child items being edited
    const [item, parent] = findObjectByKey(locations, polygon.options?.key, true) || [];
    if (item && parent && parent.polygon_coordinates) {
        editingParentItem.value = parent;
        showBoundaryMask(parent);
    }

    drawControl._toolbars.edit._modes.edit.handler.enable();
};

const cancelEdit = () => {
    drawControl._toolbars.edit._modes.edit.handler.revertLayers();
    drawControl._toolbars.edit._modes.edit.handler.disable();
    editMode.value = false;
    removeBoundaryMask();
    editingParentItem.value = null;
};

const movingChildOriginals = ref([]); // [{layer, latlngs}]
const lastDragLatLng = ref(null);

const collectChildLayers = (item) => {
    const result = [];
    if (!item.children) return result;
    for (const child of item.children) {
        const layer = findLayer(child.key);
        if (layer) {
            result.push({layer, latlngs: JSON.parse(JSON.stringify(layer.getLatLngs()))});
        }
        result.push(...collectChildLayers(child));
    }
    return result;
};

const movePolygon = (polygon) => {
    if (editMode.value) {
        showSnackbar("Finish editing before moving", 'error');
        return;
    }
    moveMode.value = true;
    movingPolygon.value = polygon;
    movingOriginalLatLngs.value = JSON.parse(JSON.stringify(polygon.getLatLngs()));
    polygon.off("dragend");
    polygon.off("drag");
    polygon.dragging.enable();

    // Collect child layers and their original positions
    const item = findObjectByKey(locations, polygon.options.key);
    movingChildOriginals.value = item ? collectChildLayers(item) : [];
    lastDragLatLng.value = polygon.getLatLngs()[0][0];

    // Move children in real-time during drag
    polygon.on('drag', () => {
        const current = polygon.getLatLngs()[0][0];
        const dLat = current.lat - lastDragLatLng.value.lat;
        const dLng = current.lng - lastDragLatLng.value.lng;
        lastDragLatLng.value = {lat: current.lat, lng: current.lng};

        for (const {layer} of movingChildOriginals.value) {
            const shifted = layer.getLatLngs()[0].map(ll => L.latLng(ll.lat + dLat, ll.lng + dLng));
            layer.setLatLngs(shifted);
        }
    });

    polygon.on('dragend', async () => {
        polygon.dragging.disable();
        polygon.off("drag");

        const [location, parent] = findObjectByKey(locations, polygon.options.key, true);
        // Only validate against parent boundary, not children - children moved rigidly with parent
        if (!validatePolygon(polygon, parent)) {
            // Revert parent and all children
            polygon.setLatLngs(movingOriginalLatLngs.value);
            for (const {layer, latlngs} of movingChildOriginals.value) {
                layer.setLatLngs(latlngs);
            }
            moveMode.value = false;
            movingPolygon.value = null;
            movingOriginalLatLngs.value = null;
            movingChildOriginals.value = [];
            return;
        }

        // Calculate total drag offset from first vertex
        const origFirst = movingOriginalLatLngs.value[0][0];
        const newFirst = polygon.getLatLngs()[0][0];
        const latOffset = newFirst.lat - origFirst.lat;
        const lngOffset = newFirst.lng - origFirst.lng;

        const url = buildApiUrl(location, parent);
        try {
            await updatePolygon(url, {
                name: location.name,
                location_changed: true,
                type: location.type_id ?? null,
                polygon_coordinates: polygon.getLatLngs()[0],
                lat_offset: latOffset,
                lng_offset: lngOffset,
            }, location.key);

            // Update tree data coordinates for children (map layers already moved)
            if (location.children) {
                const updateTreeCoords = (items) => {
                    for (const child of items) {
                        if (child.polygon_coordinates) {
                            child.polygon_coordinates.forEach(c => {
                                c.latitude += latOffset;
                                c.longitude += lngOffset;
                            });
                        }
                        if (child.children) updateTreeCoords(child.children);
                    }
                };
                updateTreeCoords(location.children);
            }
        } catch {
            // Revert parent and all children
            polygon.setLatLngs(movingOriginalLatLngs.value);
            for (const {layer, latlngs} of movingChildOriginals.value) {
                layer.setLatLngs(latlngs);
            }
        } finally {
            moveMode.value = false;
            movingPolygon.value = null;
            movingOriginalLatLngs.value = null;
            movingChildOriginals.value = [];
        }
    });
};


const cancelMove = () => {
    if (movingPolygon.value && movingOriginalLatLngs.value) {
        movingPolygon.value.setLatLngs(movingOriginalLatLngs.value);
        movingPolygon.value.dragging.disable();
        movingPolygon.value.off("dragend");
        movingPolygon.value.off("drag");
        // Revert children
        for (const {layer, latlngs} of movingChildOriginals.value) {
            layer.setLatLngs(latlngs);
        }
    }
    moveMode.value = false;
    movingPolygon.value = null;
    movingOriginalLatLngs.value = null;
    movingChildOriginals.value = [];
};

// --- Auto-Fill Parking Spaces ---
const openAutoFillDialog = () => {
    if (editMode.value || moveMode.value) {
        showSnackbar('Finish current operation first', 'error');
        return;
    }
    autoFillZone.value = selectedItemForAction.value;
    autoFillPreviewCount.value = 0;
    autoFillForm.count = 10;
    autoFillForm.rows = 2;
    autoFillForm.prefix = 'P';
    autoFillForm.type = 0;
    autoFillForm.orientation = 0;
    autoFillDialog.value = true;
};

/**
 * Generate parking spot rectangles inside a zone polygon.
 *
 * Algorithm:
 * 1. Find the longest edge of the parent polygon to determine primary axis
 * 2. Project all vertices into the rotated coordinate system (oriented bounding box)
 * 3. Size spots to fill the OBB dimensions divided by cols × rows
 * 4. Rotate each rectangle back to map coordinates
 * 5. Filter out spots whose center falls outside the parent polygon
 * 6. Return up to `count` spot polygons
 */
const generateParkingSpots = (zoneLayer, count, rows, orientationDeg) => {
    const coords = zoneLayer.getLatLngs()[0];
    if (!coords || coords.length < 3) return [];

    // Get bounding box center for aspect ratio correction
    const bounds = zoneLayer.getBounds();
    const centerLat = (bounds.getSouth() + bounds.getNorth()) / 2;
    const centerLng = (bounds.getWest() + bounds.getEast()) / 2;
    const lngScale = Math.cos(centerLat * Math.PI / 180);

    // Find longest edge to determine primary axis angle (in corrected space)
    let maxLen = 0, axisAngle = 0;
    for (let i = 0; i < coords.length; i++) {
        const a = coords[i], b = coords[(i + 1) % coords.length];
        const dLat = b.lat - a.lat;
        const dLng = (b.lng - a.lng) * lngScale;
        const len = Math.sqrt(dLat * dLat + dLng * dLng);
        if (len > maxLen) {
            maxLen = len;
            axisAngle = Math.atan2(dLng, dLat);
        }
    }

    // Add user orientation offset
    const totalAngle = axisAngle + (orientationDeg * Math.PI / 180);
    const cosA = Math.cos(totalAngle), sinA = Math.sin(totalAngle);

    // Project polygon vertices into the rotated coordinate system to get OBB
    const projected = coords.map(c => {
        const dx = c.lat - centerLat;
        const dy = (c.lng - centerLng) * lngScale;
        return {x: dx * cosA + dy * sinA, y: -dx * sinA + dy * cosA};
    });

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of projected) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
    }

    const obbWidth = maxX - minX;
    const obbHeight = maxY - minY;
    const obbCenterX = (minX + maxX) / 2;
    const obbCenterY = (minY + maxY) / 2;

    // Calculate grid layout
    const cols = Math.ceil(count / rows);
    const gapFactor = 0.06; // 6% gap between spots
    const margin = 0.95; // use 95% of the OBB

    // Size spots to fill the oriented bounding box
    const spotWidth = (obbWidth * margin) / (cols + (cols - 1) * gapFactor);
    const spotHeight = (obbHeight * margin) / (rows + (rows - 1) * gapFactor);

    // Step sizes
    const stepX = spotWidth * (1 + gapFactor);
    const stepY = spotHeight * (1 + gapFactor);

    // Total grid size
    const gridW = cols * spotWidth + (cols - 1) * spotWidth * gapFactor;
    const gridH = rows * spotHeight + (rows - 1) * spotHeight * gapFactor;

    // Start offset (top-left of grid, centered on OBB center)
    const startX = obbCenterX - gridW / 2;
    const startY = obbCenterY - gridH / 2;

    // Rotate from OBB space back to map coordinates
    const toMap = (rx, ry) => [
        centerLat + rx * cosA - ry * sinA,
        centerLng + (rx * sinA + ry * cosA) / lngScale,
    ];

    const parentCoords = coords.map(c => [c.lat, c.lng]);
    const spots = [];

    for (let r = 0; r < rows && spots.length < count; r++) {
        for (let c = 0; c < cols && spots.length < count; c++) {
            const cx = startX + c * stepX + spotWidth / 2;
            const cy = startY + r * stepY + spotHeight / 2;
            const hw = spotWidth / 2 * 0.96;  // slightly inset for visual gap
            const hh = spotHeight / 2 * 0.96;

            const corners = [
                toMap(cx - hw, cy - hh),
                toMap(cx + hw, cy - hh),
                toMap(cx + hw, cy + hh),
                toMap(cx - hw, cy + hh),
            ];

            // Check center is inside parent polygon
            const center = toMap(cx, cy);
            if (pointInPolygon(parentCoords, center)) {
                spots.push(corners.map(([lat, lng]) => ({lat, lng})));
            }
        }
    }

    return spots;
};

const previewAutoFill = () => {
    clearAutoFillPreview();

    autoFillForm.count = Math.max(1, Math.min(200, Math.floor(autoFillForm.count || 1)));
    autoFillForm.rows = Math.max(1, Math.min(autoFillForm.count, Math.floor(autoFillForm.rows || 1)));
    if (!autoFillZone.value) return;

    const zoneLayer = findLayer(autoFillZone.value.key);
    if (!zoneLayer) {
        showSnackbar('Zone not found on map', 'error');
        return;
    }

    const spots = generateParkingSpots(zoneLayer, autoFillForm.count, autoFillForm.rows, autoFillForm.orientation);
    autoFillPreviewCount.value = spots.length;

    spots.forEach((coords, i) => {
        const polygon = L.polygon(coords, {
            color: '#4CAF50',
            weight: 2,
            fillOpacity: 0.3,
            dashArray: '4, 4',
        });
        polygon.bindTooltip(`${autoFillForm.prefix}-${i + 1}`, {
            permanent: true,
            direction: 'center',
            className: 'polygon-label',
        });
        polygon.addTo(map.value);
        autoFillPreviewLayers.push(polygon);
    });

    // Fit map to zone
    centerMap(autoFillZone.value);
};

const clearAutoFillPreview = () => {
    autoFillPreviewLayers.forEach(layer => {
        if (map.value.hasLayer(layer)) map.value.removeLayer(layer);
    });
    autoFillPreviewLayers.length = 0;
    autoFillPreviewCount.value = 0;
};

const cancelAutoFill = () => {
    clearAutoFillPreview();
    autoFillDialog.value = false;
    autoFillZone.value = null;
};

const confirmAutoFill = async () => {
    if (!autoFillZone.value || autoFillPreviewLayers.length === 0) return;

    saving.value = true;
    const zone = autoFillZone.value;

    // Load existing children first if not yet loaded
    if (zone.child_count && !zone._childrenLoaded) {
        try {
            await fetchChildren(zone);
        } catch { /* continue anyway */ }
    }
    const spotsData = autoFillPreviewLayers.map((layer, i) => ({
        name: `${autoFillForm.prefix}-${i + 1}`,
        type: autoFillForm.type,
        polygon_coordinates: layer.getLatLngs()[0].map(ll => ({lat: ll.lat, lng: ll.lng})),
    }));

    try {
        const response = await send('post', `companies/locations/zones/${zone.id}/spots-batch`, {spots: spotsData});
        if (response.data.success) {
            clearAutoFillPreview();

            const createdSpots = response.data.data;
            if (!zone.children) zone.children = [];

            createdSpots.forEach(data => {
                const spot = {
                    ...data,
                    parent: zone.key,
                    icon: 'mdi-map-marker-circle',
                    model: 'spot',
                    key: `spot-${data.id}`,
                    children: null,
                    child_count: 0,
                };
                zone.children.push(spot);
                drawPolygonOnMap(spot);
            });
            zone.child_count = (zone.child_count || 0) + createdSpots.length;
            zone._childrenLoaded = true;

            drawnItems.addTo(map.value);
            opened.value = [zone.parent, zone.key];
            autoFillDialog.value = false;
            autoFillZone.value = null;
            showSnackbar(response.data.message);
        }
    } catch (errorResponse) {
        handleApiError(errorResponse);
    } finally {
        saving.value = false;
    }
};

// --- Boundary Mask (dims area outside parent) ---
const showBoundaryMask = (parentItem) => {
    removeBoundaryMask();
    const parentLayer = findLayer(parentItem.key);
    if (!parentLayer) return;

    const parentCoords = parentLayer.getLatLngs()[0];
    // World bounds as outer ring, parent as hole (inverted polygon)
    const worldBounds = [L.latLng(-90, -180), L.latLng(-90, 180), L.latLng(90, 180), L.latLng(90, -180)];
    boundaryMask.value = L.polygon([worldBounds, parentCoords], {
        color: 'transparent',
        fillColor: '#000',
        fillOpacity: 0.45,
        interactive: true,  // Block clicks outside parent
        bubblingMouseEvents: false,
    });
    boundaryMask.value.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        showSnackbar('Click inside the highlighted boundary', 'warning');
    });
    boundaryMask.value.addTo(map.value);
};

const removeBoundaryMask = () => {
    if (boundaryMask.value) {
        if (map.value?.hasLayer(boundaryMask.value)) map.value.removeLayer(boundaryMask.value);
        boundaryMask.value = null;
    }
};

// --- Polygon Validation (Ray-casting) ---
const isChildInsideParent = (parent, child) => {
    const parentCoords = parent.map(p => [p.lat, p.lng]);
    const childCoords = child.map(c => [c.lat, c.lng]);
    return childCoords.every(point => pointInPolygon(parentCoords, point));
};

const pointInPolygon = (polygon, point) => {
    let inside = false;
    const x = parseFloat(point[0]), y = parseFloat(point[1]);
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = parseFloat(polygon[i][0]), yi = parseFloat(polygon[i][1]);
        const xj = parseFloat(polygon[j][0]), yj = parseFloat(polygon[j][1]);
        if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
            inside = !inside;
        }
    }
    return inside;
};

const validatePolygon = (polygon, parent = null, location = null) => {
    if (parent && typeof parent === 'object' && parent.polygon_coordinates) {
        const valid = isChildInsideParent(
            parent.polygon_coordinates.map(c => ({lat: c.latitude, lng: c.longitude})),
            polygon.getLatLngs()[0]
        );
        if (!valid) {
            showSnackbar("Polygon must stay within the parent boundary.", 'error');
            return false;
        }
    }
    if (location?.children) {
        const checkChildren = (children) => {
            for (const child of children) {
                // Use map layer positions (always accurate) over tree data (can be stale)
                const childLayer = findLayer(child.key);
                const childCoords = childLayer
                    ? childLayer.getLatLngs()[0]
                    : (child.polygon_coordinates?.length
                        ? child.polygon_coordinates.map(c => ({lat: c.latitude, lng: c.longitude}))
                        : null);
                if (childCoords) {
                    const valid = isChildInsideParent(polygon.getLatLngs()[0], childCoords);
                    if (!valid) {
                        showSnackbar("Polygon would exclude existing children.", 'error');
                        return false;
                    }
                }
                // Recurse into grandchildren
                if (child.children && !checkChildren(child.children)) return false;
            }
            return true;
        };
        if (!checkChildren(location.children)) return false;
    }
    return true;
};

// --- API Helpers ---
const buildApiUrl = (item, parent = null) => {
    if (!parent) {
        if (item.model === 'location') return `companies/locations/${item.id}`;
        // Fallback: parse parent from key
        if (item.model === 'zone') return `companies/locations/${item.parent.replace('location-', '')}/zones/${item.id}`;
        if (item.model === 'spot') return `companies/locations/zones/${item.parent.replace('zone-', '')}/spots/${item.id}`;
    }
    if (parent.model === 'location') return `companies/locations/${parent.id}/zones/${item.id}`;
    if (parent.model === 'zone') return `companies/locations/zones/${parent.id}/spots/${item.id}`;
    return `companies/locations/${item.id}`;
};

const updatePolygon = (url, data, itemKey = null) => {
    return send("put", url, data)
        .then((response) => {
            if (response.data.success) {
                if (itemKey) {
                    const item = findObjectByKey(locations, itemKey);
                    if (item) item.polygon_coordinates = response.data.data.polygon_coordinates;
                }
                showSnackbar(response.data.message);
            }
        })
        .catch((errorResponse) => {
            handleApiError(errorResponse);
            throw errorResponse;
        });
};

const confirmDelete = () => {
    if (!deleteTarget.value) return;
    deleting.value = true;

    const target = deleteTarget.value;
    const url = buildApiUrl(target);

    send("delete", url)
        .then((response) => {
            if (response.data.success) {
                // Remove child layers from map
                const item = findObjectByKey(locations, target.key);
                if (item?.children?.length) {
                    for (const child of item.children) {
                        if (child.children?.length) {
                            child.children.forEach(gc => findAndDeleteLayer(gc.key));
                        }
                        findAndDeleteLayer(child.key);
                    }
                }
                findAndDeleteLayer(target.key);

                // Decrement parent's child_count
                if (target.parent) {
                    const parentItem = findObjectByKey(locations, target.parent);
                    if (parentItem && parentItem.child_count > 0) {
                        parentItem.child_count--;
                    }
                }

                deleteObjectByKey(locations, target.key);

                if (selectedItem.value?.key === target.key) {
                    selectedItem.value = null;
                }
                deleteDialog.value = false;
                showSnackbar(response.data.message);
            }
        })
        .catch((errorResponse) => {
            handleApiError(errorResponse);
        })
        .finally(() => {
            deleting.value = false;
        });
};

const handleApiError = (errorResponse) => {
    if (errorResponse?.response?.data?.errors) {
        const errors = errorResponse.response.data.errors;
        Object.keys(errors).forEach(key => showSnackbar(errors[key][0], "error"));
    } else if (errorResponse?.response?.data?.message) {
        showSnackbar(errorResponse.response.data.message, "error");
    } else {
        showSnackbar("Something went wrong", "error");
    }
};

// --- Tree Helpers ---
const findObjectByKey = (tree, key, returnParent = false) => {
    for (const node of tree) {
        if (node.key === key) {
            return returnParent ? [node, returnParent === true ? null : returnParent] : node;
        }
        if (node.children) {
            const result = findObjectByKey(node.children, key, returnParent ? node : false);
            if (result) return result;
        }
    }
    return null;
};

const deleteObjectByKey = (tree, key) => {
    for (let i = 0; i < tree.length; i++) {
        if (tree[i].key === key) {
            tree.splice(i, 1);
            return true;
        }
        if (tree[i].children) {
            if (deleteObjectByKey(tree[i].children, key)) return true;
        }
    }
    return false;
};

const findLayer = (key) => {
    for (const id in drawnItems._layers) {
        if (drawnItems._layers[id].options?.key === key) return drawnItems._layers[id];
    }
    return null;
};

const findAndDeleteLayer = (key) => {
    const layer = findLayer(key);
    if (layer) drawnItems.removeLayer(layer);
};
</script>

<style>
.location-manager {
    height: calc(100vh - 64px);
}

.location-manager .full-card {
    height: 100%;
}

.tree-item-row {
    cursor: pointer;
    min-height: 32px;
    padding: 2px 0;
}

.tree-item-row:hover {
    background: rgba(0, 0, 0, 0.04);
    border-radius: 4px;
}

.map-layer-toggle {
    /* Bottom left: the address bar is centred along the top and the
       fullscreen button sits bottom right. */
    position: absolute;
    bottom: 12px;
    left: 12px;
    z-index: 1000;
}

/* On a phone the page footer covers the bottom of the map; sit under the
   address bar instead, clear of the zoom and draw buttons on the left. */
@media (max-width: 600px) {
    .map-layer-toggle {
        top: 56px;
        bottom: auto;
        left: 56px;
    }
}

.map-toolbar {
    position: absolute;
    top: 12px;
    left: 60px;
    z-index: 1000;
    display: flex;
    gap: 4px;
}

.polygon-label {
    background: rgba(0, 0, 0, 0.7) !important;
    border: none !important;
    color: white !important;
    font-size: 12px !important;
    padding: 2px 8px !important;
    border-radius: 4px !important;
    box-shadow: none !important;
}

.polygon-label::before {
    border-top-color: rgba(0, 0, 0, 0.7) !important;
}

.v-treeview-item {
    min-height: 36px;
}

path.leaflet-path-draggable:hover {
    cursor: move;
}

.leaflet-draw-toolbar a {
    background-size: 270px 30px;
}
</style>
